import { useState, useEffect, useRef, useCallback } from 'react';

import Button from '../../components/display/buttons/button';
import CircleSpinner from '../../components/display/loaders/circle-spinner';

// eslint-disable-next-line
function useInfiniteScroll<T>(
    initial: T[],
    fetcher: (limit: number, page: number) => Promise<T[] | null>,
    limit = 12,
    showCustomLoader = false,
) {
    const [entities, setEntities] = useState<T[]>(initial);
    const observer = useRef<IntersectionObserver | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({
        retry: 0,
        page: initial.length % limit === 0 ? initial.length / limit + 1 : 1,
        hasNext: initial.length === 0 || initial.length % limit === 0,
        shouldFetchNext: false,
    });

    const { page, retry, shouldFetchNext, hasNext } = pagination;

    useEffect(() => {
        setEntities(initial);
        setPagination({
            page: initial.length % limit === 0 ? initial.length / limit + 1 : 1,
            retry: 0,
            shouldFetchNext: false,
            hasNext: initial.length === 0 || initial.length % limit === 0,
        });
    }, [initial, limit]);

    const lastElementRef = useCallback((node: HTMLDivElement) => {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0]?.isIntersecting) {
                setPagination(prev => {
                    if (prev.hasNext && !prev.retry) {
                        return { ...prev, shouldFetchNext: true };
                    }
                    return prev;
                });
            }
        });
        if (node) observer.current.observe(node);
    }, []);

    useEffect(() => {
        if (hasNext && shouldFetchNext && !isLoading) {
            const fetchMore = async () => {
                setIsLoading(true);

                const response = await fetcher(limit, page);
                if (response && response.length) {
                    setEntities(prevEntities => [...prevEntities, ...response]);
                    setPagination({
                        page: response.length === limit ? page + 1 : page,
                        retry: 0,
                        hasNext: response.length === limit,
                        shouldFetchNext: false,
                    });
                } else {
                    setPagination(prev => ({
                        ...prev,
                        retry: prev.retry > 0 ? 2 : 1,
                        hasNext: prev.retry < 2,
                        shouldFetchNext: false,
                    }));
                }

                setIsLoading(false);
            };
            fetchMore();
        }
    }, [page, hasNext, shouldFetchNext, isLoading, limit, entities]);

    const renderScrollTarget = useCallback(() => {
        return (
            <div ref={lastElementRef}>
                {!showCustomLoader && (
                    <>
                        {retry === 1 && !isLoading && (
                            <div className="w-full p-20 flex items-center justify-center">
                                <Button onClick={() => setPagination(prev => ({ ...prev, shouldFetchNext: true }))}>
                                    Load More
                                </Button>
                            </div>
                        )}
                        {isLoading && (
                            <div className="w-full p-20 flex items-center justify-center">
                                <CircleSpinner className="w-10 h-10" />
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }, [isLoading, retry, lastElementRef]);
    return { entities, lastElementRef, isLoading, renderScrollTarget };
}

export default useInfiniteScroll;
